import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Lock, User, LogIn, Building2, AlertCircle } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { API } from '../utils/api';

const TenantLoginPage = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    remember_me: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Cambia manifest per PWA tenant
  useEffect(() => {
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = '/manifest-tenant.json';
    }
    
    // Cambia theme-color per tenant
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.content = '#06b6d4';
    }
    
    // Cambia apple-mobile-web-app-title
    let appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitleMeta) {
      appleTitleMeta = document.createElement('meta');
      appleTitleMeta.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleTitleMeta);
    }
    appleTitleMeta.content = 'Il Mio Ristorante';
    
    return () => {
      // Ripristina manifest originale quando si esce
      if (manifestLink) {
        manifestLink.href = '/manifest.json';
      }
    };
  }, []);

  // Carica credenziali salvate al mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem('tenant_saved_credentials');
    if (savedCredentials) {
      try {
        const decoded = atob(savedCredentials);
        const credentials = JSON.parse(decoded);
        setFormData({
          username: credentials.username || '',
          password: credentials.password || '',
          remember_me: true
        });
      } catch (error) {
        console.error('Error loading saved credentials:', error);
        localStorage.removeItem('tenant_saved_credentials');
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.username.trim()) {
      setError('Inserisci il nome utente');
      return;
    }
    if (!formData.password.trim()) {
      setError('Inserisci la password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/saas/auth/login`, {
        username: formData.username.trim(),
        password: formData.password
      });
      
      // Salva token tenant
      localStorage.setItem('tenant_token', response.data.access_token);
      localStorage.setItem('tenant_refresh_token', response.data.refresh_token || '');
      localStorage.setItem('tenant_user', JSON.stringify(response.data.user));
      localStorage.setItem('tenant_list', JSON.stringify(response.data.tenants));
      
      if (formData.remember_me) {
        const credentials = {
          username: formData.username,
          password: formData.password
        };
        const encoded = btoa(JSON.stringify(credentials));
        localStorage.setItem('tenant_saved_credentials', encoded);
      } else {
        localStorage.removeItem('tenant_saved_credentials');
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      const userName = response.data.user.nome || response.data.user.username;
      toast.success(`Benvenuto ${userName}!`);
      
      onLogin({
        ...response.data.user,
        tenants: response.data.tenants,
        token: response.data.access_token
      });
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Errore di connessione al server';
      const statusCode = error.response?.status;
      
      // Check se abbonamento scaduto
      if (statusCode === 403 && errorMessage.toLowerCase().includes('scaduto')) {
        setError('Abbonamento scaduto');
        toast.error('Il tuo abbonamento è scaduto. Contatta l\'amministratore per rinnovare.', {
          duration: 6000,
          icon: '⚠️'
        });
      } else if (errorMessage.toLowerCase().includes('password')) {
        setError('Password non corretta');
        toast.error(errorMessage);
      } else if (errorMessage.toLowerCase().includes('username') || errorMessage.toLowerCase().includes('credenziali')) {
        setError('Credenziali non valide');
        toast.error(errorMessage);
      } else {
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgetCredentials = () => {
    localStorage.removeItem('tenant_saved_credentials');
    setFormData({
      username: '',
      password: '',
      remember_me: false
    });
    toast.success('Credenziali dimenticate');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/images/login-bg.png)' }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Toaster per notifiche */}
      <Toaster position="top-center" richColors />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo e Header */}
        <div className="text-center mb-6">
          <div className="relative inline-block mb-3">
            <img 
              src="/images/logo-ristorantissimo.png" 
              alt="Ristorantissimo Logo" 
              className="h-64 w-auto object-contain drop-shadow-2xl"
            />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Building2 className="h-5 w-5 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-medium tracking-wider uppercase">Accesso Ristorante</span>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-zinc-900/80 backdrop-blur-xl rounded-3xl border border-cyan-500/20 shadow-2xl shadow-cyan-500/5 overflow-hidden">
          {/* Card Header */}
          <div className="px-8 pt-8 pb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Lock className="h-5 w-5 text-black" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Accedi al tuo Ristorante</h2>
                <p className="text-sm text-gray-400">Inserisci le tue credenziali</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 pb-8 space-y-5">
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Username */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">
                Username
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <User className="h-5 w-5 text-slate-500" />
                </div>
                <input
                  type="text"
                  placeholder="Inserisci username..."
                  value={formData.username}
                  onChange={(e) => {
                    setError('');
                    setFormData({...formData, username: e.target.value});
                  }}
                  className={`w-full h-14 pl-12 pr-4 bg-slate-800/50 border rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all ${
                    error && error.toLowerCase().includes('utente') ? 'border-red-500' : 'border-slate-700'
                  }`}
                  data-testid="tenant-username-input"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">
                Password
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <Lock className="h-5 w-5 text-slate-500" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Inserisci password..."
                  value={formData.password}
                  onChange={(e) => {
                    setError('');
                    setFormData({...formData, password: e.target.value});
                  }}
                  className={`w-full h-14 pl-12 pr-12 bg-slate-800/50 border rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all ${
                    error && error.toLowerCase().includes('password') ? 'border-red-500' : 'border-slate-700'
                  }`}
                  data-testid="tenant-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.remember_me}
                  onChange={(e) => setFormData({...formData, remember_me: e.target.checked})}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
                />
                <span className="text-sm text-slate-400">Ricordami</span>
              </label>
              {formData.remember_me && (
                <button
                  type="button"
                  onClick={handleForgetCredentials}
                  className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  Dimentica credenziali
                </button>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-black font-semibold rounded-xl shadow-lg shadow-cyan-500/30 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="tenant-login-button"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Accedi
                </>
              )}
            </button>

            {/* Link to admin login */}
            <div className="text-center pt-4 border-t border-slate-700/50">
              <p className="text-sm text-slate-400">
                Sei un amministratore?{' '}
                <a 
                  href="/admin" 
                  className="text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  Accedi qui
                </a>
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-6">
          © 2025 Ristorantissimo - Tutti i diritti riservati
        </p>
      </div>
    </div>
  );
};

export default TenantLoginPage;
